import { useState } from 'react'
import { AirlineLogo, carrierTheme, normaliseCarrier } from './AirlineLogo'
import { airportTimeZone, lookupAirport } from '../lib/airports'
import type { Flight, TourCrew, WithId } from '../types/models'

/**
 * A flight, drawn the way the native card draws it.
 *
 * Air New Zealand and Virgin get their own near-black and navy surfaces, which
 * is the one place in the app a card ignores the skin: those carriers' marks
 * are near-black and would vanish on a light panel. Everything else sits on the
 * ordinary panel.
 *
 * Clicking opens the card rather than the editor — passengers, baggage total
 * and the route detail — with Edit and Delete as deliberate second steps.
 */
export default function FlightCard({
  flight,
  crew,
  canEdit,
  onEdit,
  onDelete,
  compact = false,
}: {
  flight: WithId<Flight>
  crew: WithId<TourCrew>[]
  canEdit: boolean
  onEdit: () => void
  onDelete: () => void
  /** Side-rail variant: tighter type and no expand. Carrier theming still
   *  applies — an Air NZ flight is a black card wherever it appears. */
  compact?: boolean
}) {
  const [open, setOpen] = useState(false)

  const carrier = normaliseCarrier(flight.airline ?? flight.flightNumber)
  // Carrier theming applies in both variants — an Air NZ flight is a black
  // card wherever it appears, including the side rail.
  const theme = carrierTheme(carrier)

  const departureZone = airportTimeZone(flight.origin)
  const arrivalZone = airportTimeZone(flight.destination)
  const originAirport = lookupAirport(flight.origin)
  const destinationAirport = lookupAirport(flight.destination)

  const surface = theme
    ? { background: theme.background, color: theme.text, borderColor: 'transparent' }
    : {}
  const mutedColour = theme ? theme.muted : 'var(--text-secondary)'

  const totalBaggage = flight.passengers.reduce(
    (total, passenger) => total + (Number(passenger.baggage ?? '0') || 0),
    0,
  )

  if (compact) {
    return (
      <div className="panel panel-pad stack" style={{ gap: 10, ...surface }}>
        <span className="t-spec-caption" style={{ color: mutedColour }}>
          {[flight.airline, flight.flightNumber].filter(Boolean).join(' · ') || 'Flight'}
        </span>
        <div className="spread" style={{ gap: 12 }}>
          <span className="t-display-m" style={{ whiteSpace: 'nowrap' }}>
            {flight.origin} — {flight.destination}
          </span>
          {/* The roundel, not the wordmark — a horizontal lockup shrinks its
              type to nothing at this width. Sized to carry the card. */}
          <AirlineLogo code={carrier} variant="icon" size={44} onDark={!!theme} />
        </div>
        <div
          className="row t-spec-caption"
          style={{ gap: 8, flexWrap: 'wrap', color: mutedColour }}
        >
          <span>{formatStamp(flight.departureTimeUTC.toDate(), departureZone)}</span>
          <Rule />
          <span>{duration(flight)}</span>
          <Rule />
          <span>{formatStamp(flight.arrivalTimeUTC.toDate(), arrivalZone)}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="panel" style={{ overflow: 'hidden', ...surface }}>
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: 16,
          display: 'block',
          color: 'inherit',
        }}
      >
        <div className="spread" style={{ alignItems: 'flex-start' }}>
          <div className="stack" style={{ gap: 2, minWidth: 0 }}>
            <span className="t-spec-caption" style={{ color: mutedColour }}>
              {[flight.airline, flight.flightNumber].filter(Boolean).join(' · ') || 'Flight'}
            </span>

            <span
              className="t-display-l"
              style={{ letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}
            >
              {flight.origin} — {flight.destination}
            </span>
          </div>

          <AirlineLogo code={carrier} variant="icon" size={68} onDark={!!theme} />
        </div>

        <div
          className="row t-spec-caption"
          style={{ gap: 10, marginTop: 12, color: mutedColour, flexWrap: 'wrap' }}
        >
          <span aria-hidden>↑</span>
          <span>{formatStamp(flight.departureTimeUTC.toDate(), departureZone)}</span>
          <Rule />
          <span>{duration(flight)}</span>
          <Rule />
          <span>{formatStamp(flight.arrivalTimeUTC.toDate(), arrivalZone)}</span>
          <span aria-hidden>↓</span>
        </div>
      </button>

      {open && (
        <div
          className="stack"
          style={{
            padding: '0 16px 16px',
            gap: 14,
            borderTop: `1px solid ${theme ? 'rgba(255,255,255,0.14)' : 'var(--hairline)'}`,
            paddingTop: 14,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Leg
              eyebrow="Departs"
              code={flight.origin}
              place={originAirport}
              stamp={formatFull(flight.departureTimeUTC.toDate(), departureZone)}
              muted={mutedColour}
            />
            <Leg
              eyebrow="Arrives"
              code={flight.destination}
              place={destinationAirport}
              stamp={formatFull(flight.arrivalTimeUTC.toDate(), arrivalZone)}
              muted={mutedColour}
            />
          </div>

          {flight.passengers.length > 0 && (
            <div className="stack" style={{ gap: 7 }}>
              <span className="t-eyebrow" style={{ color: mutedColour }}>
                Passengers &amp; baggage
              </span>
              {flight.passengers.map((passenger) => (
                <div key={passenger.crewId} className="spread">
                  <span className="t-body">
                    {crew.find((member) => member.id === passenger.crewId)?.name ?? 'Unknown crew'}
                  </span>
                  {passenger.baggage && (
                    <span className="t-spec-caption" style={{ color: mutedColour }}>
                      {passenger.baggage} kg
                    </span>
                  )}
                </div>
              ))}
              <div
                className="spread"
                style={{
                  paddingTop: 7,
                  borderTop: `1px solid ${theme ? 'rgba(255,255,255,0.14)' : 'var(--hairline)'}`,
                  fontWeight: 600,
                }}
              >
                <span>Total baggage</span>
                <span className="t-spec">{totalBaggage} kg</span>
              </div>
            </div>
          )}

          {flight.notes && (
            <p className="t-caption" style={{ color: mutedColour }}>
              {flight.notes}
            </p>
          )}

          {canEdit && (
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn-secondary" onClick={onEdit} style={{ flex: 1 }}>
                Edit flight
              </button>
              <button className="btn btn-danger" onClick={onDelete} style={{ flex: 1 }}>
                Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Leg({
  eyebrow,
  code,
  place,
  stamp,
  muted,
}: {
  eyebrow: string
  code: string
  place: ReturnType<typeof lookupAirport>
  stamp: string
  muted: string
}) {
  return (
    <div className="stack" style={{ gap: 2 }}>
      <span className="t-eyebrow" style={{ color: muted }}>
        {eyebrow}
      </span>
      <span className="t-display-s">{code}</span>
      {place && (
        <span className="t-caption" style={{ color: muted }}>
          {place.city}
          {place.countryCode ? `, ${place.countryCode}` : ''}
        </span>
      )}
      <span className="t-spec-caption" style={{ color: muted }}>
        {stamp}
      </span>
    </div>
  )
}

function Rule() {
  return (
    <span
      aria-hidden
      style={{ width: 18, height: 1, background: 'currentColor', opacity: 0.3 }}
    />
  )
}

/** "19:20, 8 AUG" — local to that end of the flight. */
function formatStamp(date: Date, timeZone: string | null): string {
  if (!timeZone) return '—'
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    day: 'numeric',
    month: 'short',
    timeZone,
  })
    .format(date)
    .toUpperCase()
}

function formatFull(date: Date, timeZone: string | null): string {
  if (!timeZone) return '—'
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).format(date)
}

/** "6H 15M" — real elapsed time, so it's right across zones and the dateline. */
function duration(flight: WithId<Flight>): string {
  const seconds =
    (flight.arrivalTimeUTC.toMillis() - flight.departureTimeUTC.toMillis()) / 1000
  if (seconds <= 0) return '—'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${hours}H ${minutes}M`
}
